import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Copy, Trash, ArrowsClockwise, Eye, ClockCounterClockwise } from "@phosphor-icons/react";

const Step = ({ n, children }) => (
  <li className="text-sm text-[#52525B]"><b className="text-[#000000]">{n}.</b> {children}</li>
);

export default function DemoRepsPanel() {
  const [reps, setReps] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", organization: "", language: "en" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/demo-reps").then(({ data }) => setReps(data)).catch(() => setReps([]));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-reps", form);
      toast.success(data.email_sent ? `Invite emailed to ${form.email}` : "Evaluator created — email could not be sent, use Copy link instead");
      setForm({ name: "", email: "", organization: "", language: "en" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copyLink = async (rep) => {
    try { await navigator.clipboard.writeText(rep.login_link); toast.success("One-click demo link copied"); } catch { /* ignore */ }
  };
  const resend = async (rep) => {
    try {
      const { data } = await api.post(`/admin/demo-reps/${rep.id}/resend`);
      toast[data.email_sent ? "success" : "error"](data.email_sent ? `Re-sent to ${rep.email}` : "Email failed to send");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const extend = async (rep) => {
    try {
      const { data } = await api.post(`/admin/demo-reps/${rep.id}/extend`);
      toast.success(`${rep.name}'s access extended — now ${data.rep.days_left} days left`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const revoke = async (rep) => {
    if (!window.confirm(`Revoke ${rep.name}'s demo access? Their link and code stop working immediately.`)) return;
    try { await api.delete(`/admin/demo-reps/${rep.id}`); toast.success("Access revoked"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="demo-reps-panel" className="space-y-4">
      <div className="rounded-xl border border-[#C4C8CE] bg-white p-4">
        <h3 className="font-display font-extrabold text-lg m-0">Demo Reps — invite evaluators</h3>
        <p className="text-sm text-[#52525B] mt-1 mb-3">
          Send a school-board rep their own evaluator access. They get an email with a one-click login link,
          their personal access code, the app install guide, and your pricing &amp; privacy pages.
        </p>
        <ol className="pl-4 space-y-1 mb-4">
          <Step n={1}>Enter their name, email, and board/organization below, then hit <b>Send invite</b>.</Step>
          <Step n={2}>They see the <b>Demo</b> version of the course: the curated sample open, the full outline visible but locked.</Step>
          <Step n={3}>Access expires automatically after <b>30 days</b> — a natural moment to follow up. You can revoke any time.</Step>
          <Step n={4}>Want different demo lessons? Toggle the <b>Demo</b> column in the Lesson Matrix tab — it updates for every evaluator instantly.</Step>
          <Step n={5}>You get a <b>text + email the moment a rep first opens the demo</b> (text goes to the alert phone in your booking settings), and reps automatically get a <b>reminder email 5 days before access ends</b> with your booking link.</Step>
        </ol>
        <form onSubmit={invite} className="grid sm:grid-cols-5 gap-2">
          <input data-testid="demo-rep-name" required className="input-base" placeholder="Full name"
            value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <input data-testid="demo-rep-email" required type="email" className="input-base" placeholder="Email"
            value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          <input data-testid="demo-rep-org" className="input-base" placeholder="Board / organization (optional)"
            value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} />
          <select data-testid="demo-rep-language" className="input-base" title="Invite email + demo language"
            value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
          <button data-testid="demo-rep-send" disabled={busy} className="btn-primary inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            <PaperPlaneTilt size={15} weight="bold" /> {busy ? "Sending…" : "Send invite"}
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-[#C4C8CE] bg-white p-4">
        <h4 className="font-display font-bold text-sm m-0 mb-2">Invited evaluators</h4>
        {!reps ? <div className="text-sm text-[#71717A]">Loading…</div>
          : reps.length === 0 ? <div className="text-sm text-[#71717A]" data-testid="demo-reps-empty">No evaluators invited yet.</div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="demo-reps-table">
              <thead>
                <tr className="text-left text-xs text-[#71717A] uppercase tracking-wide">
                  <th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Email</th><th className="py-1 pr-3">Org</th>
                  <th className="py-1 pr-3">Lang</th>
                  <th className="py-1 pr-3">Code</th><th className="py-1 pr-3">Status</th><th className="py-1 pr-3">Days left</th>
                  <th className="py-1 pr-3">Lessons</th><th className="py-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reps.map((r) => (
                  <tr key={r.id} className="border-t border-[#ECEDEF]" data-testid={`demo-rep-row-${r.id}`}>
                    <td className="py-1.5 pr-3 font-semibold">{r.name}</td>
                    <td className="py-1.5 pr-3">{r.email}</td>
                    <td className="py-1.5 pr-3">{r.organization || "—"}</td>
                    <td className="py-1.5 pr-3 font-bold" data-testid={`demo-rep-lang-${r.id}`}>{(r.language || "en").toUpperCase()}</td>
                    <td className="py-1.5 pr-3 font-mono font-bold">{r.code}</td>
                    <td className="py-1.5 pr-3">
                      {r.revoked ? <span className="text-red-600 font-bold">Revoked</span>
                        : r.activated ? <span className="text-[#3F6212] font-bold">Active</span>
                        : <span className="text-[#71717A]">Invited</span>}
                    </td>
                    <td className="py-1.5 pr-3">{r.revoked ? "—" : r.days_left}</td>
                    <td className="py-1.5 pr-3">{r.lessons_done}</td>
                    <td className="py-1.5">
                      {!r.revoked && (
                        <span className="inline-flex gap-1">
                          <button title="Copy one-click demo link" data-testid={`demo-rep-copy-${r.id}`} onClick={() => copyLink(r)}
                            className="p-1.5 rounded border border-[#C4C8CE] bg-white hover:bg-[#F4F4F5] cursor-pointer"><Copy size={14} /></button>
                          <button title="Re-send invite email" data-testid={`demo-rep-resend-${r.id}`} onClick={() => resend(r)}
                            className="p-1.5 rounded border border-[#C4C8CE] bg-white hover:bg-[#F4F4F5] cursor-pointer"><ArrowsClockwise size={14} /></button>
                          <button title="Open demo as this evaluator" data-testid={`demo-rep-open-${r.id}`} onClick={() => window.open(r.login_link, "_blank", "noopener")}
                            className="p-1.5 rounded border border-[#C4C8CE] bg-white hover:bg-[#F4F4F5] cursor-pointer"><Eye size={14} /></button>
                          <button title="Extend access +30 days" data-testid={`demo-rep-extend-${r.id}`} onClick={() => extend(r)}
                            className="px-1.5 py-1 rounded border border-[#C4C8CE] bg-white hover:bg-[#F4F4F5] cursor-pointer inline-flex items-center gap-0.5 text-[10px] font-bold"><ClockCounterClockwise size={13} />+30d</button>
                          <button title="Revoke access" data-testid={`demo-rep-revoke-${r.id}`} onClick={() => revoke(r)}
                            className="p-1.5 rounded border border-red-300 text-red-600 bg-white hover:bg-red-50 cursor-pointer"><Trash size={14} /></button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
